import React, { useCallback, useEffect, useState } from 'react';
import Breadcrumb from '@/components/Breadcrumb';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Radio, Server, Database, Users, ShieldCheck, Router as RouterIcon, ScrollText,
  Activity, RefreshCw, CheckCircle2, XCircle, Signal, UserCheck, UserX, KeyRound, Plug, Layers,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import api, { formatApiError } from '@/lib/api';
import { fmtDateTime } from './radiusUtils';
import RadiusUsers from './RadiusUsers';
import RadiusConnection from './RadiusConnection';
import RadiusGroups from './RadiusGroups';
import RadiusNas from './RadiusNas';
import RadiusNasGroups from './RadiusNasGroups';
import { AuthLogTab, SessionsTab, AuditLogTab } from './RadiusLogs';

function StatCard({ icon: Icon, label, value, tone = 'sky', sub }) {
  const tones = {
    sky: 'bg-sky-500/10 border-sky-500/30 text-sky-500',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500',
    rose: 'bg-rose-500/10 border-rose-500/30 text-rose-500',
    amber: 'bg-amber-500/10 border-amber-500/30 text-amber-500',
    violet: 'bg-violet-500/10 border-violet-500/30 text-violet-500',
  };
  return (
    <Card className="border-border"><CardContent className="p-4 flex items-center gap-3">
      <span className={`p-2.5 rounded-lg border ${tones[tone]}`}><Icon className="w-5 h-5" /></span>
      <div className="min-w-0">
        <div className="text-2xl font-bold tabular-nums leading-none">{value}</div>
        <div className="text-xs text-muted-foreground mt-1 truncate">{label}</div>
        {sub && <div className="text-[11px] text-muted-foreground/80">{sub}</div>}
      </div>
    </CardContent></Card>
  );
}

function DashboardTab({ status, loading, onRefresh }) {
  const c = status?.counts || {};
  const fr = status?.freeradius || {};
  const dbo = status?.database || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <Card className="border-border"><CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`p-2.5 rounded-lg border ${fr.online ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' : 'bg-rose-500/10 border-rose-500/30 text-rose-500'}`}><Server className="w-5 h-5" /></span>
            <div><div className="text-sm font-semibold">FreeRADIUS</div><div className="text-xs text-muted-foreground">{fr.last_auth ? `Auth terakhir: ${fmtDateTime(fr.last_auth)}` : 'Dikelola eksternal (server FreeRADIUS)'}</div></div>
          </div>
          {fr.online ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Active</Badge> : <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/30"><XCircle className="w-3 h-3 mr-1" />{fr.state === 'idle' ? 'Idle' : (fr.state || 'Unknown')}</Badge>}
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className={`p-2.5 rounded-lg border ${dbo.online ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-500' : 'bg-rose-500/10 border-rose-500/30 text-rose-500'}`}><Database className="w-5 h-5" /></span>
            <div><div className="text-sm font-semibold">Database</div><div className="text-xs text-muted-foreground">{dbo.version || dbo.name || 'RADIUS DB'}</div></div>
          </div>
          {dbo.online ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30"><CheckCircle2 className="w-3 h-3 mr-1" />Online</Badge> : <Badge className="bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-500/30"><XCircle className="w-3 h-3 mr-1" />Offline</Badge>}
        </CardContent></Card>
        <Card className="border-border"><CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="p-2.5 rounded-lg border bg-sky-500/10 border-sky-500/30 text-sky-500"><Signal className="w-5 h-5" /></span>
            <div><div className="text-sm font-semibold">Active Sessions</div><div className="text-xs text-muted-foreground">Sesi berjalan (radacct)</div></div>
          </div>
          <div className="text-2xl font-bold tabular-nums">{c.active_sessions ?? '—'}</div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={Users} label="Total Users" value={c.total_users ?? '—'} tone="sky" />
        <StatCard icon={UserCheck} label="Active Users" value={c.active_users ?? '—'} tone="emerald" />
        <StatCard icon={UserX} label="Disabled Users" value={c.disabled_users ?? '—'} tone="rose" />
        <StatCard icon={RouterIcon} label="NAS / MikroTik" value={c.total_nas ?? '—'} tone="violet" />
        <StatCard icon={CheckCircle2} label="Auth Success (hari ini)" value={c.auth_success_today ?? '—'} tone="emerald" />
        <StatCard icon={XCircle} label="Auth Failed (hari ini)" value={c.auth_failed_today ?? '—'} tone="amber" />
      </div>

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="radius-dashboard-refresh"><RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</Button>
      </div>
    </div>
  );
}

function StatisticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.get('/radius/statistics'); setData(res.data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const Block = ({ title, s }) => (
    <Card className="border-border"><CardContent className="p-4">
      <div className="text-sm font-semibold mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex justify-between border-b border-border pb-1"><span className="text-muted-foreground">Success</span><span className="font-semibold text-emerald-500">{s?.success ?? 0}</span></div>
        <div className="flex justify-between border-b border-border pb-1"><span className="text-muted-foreground">Failed</span><span className="font-semibold text-rose-500">{s?.failed ?? 0}</span></div>
        <div className="flex justify-between border-b border-border pb-1"><span className="text-muted-foreground">Total</span><span className="font-semibold">{s?.total ?? 0}</span></div>
        <div className="flex justify-between border-b border-border pb-1"><span className="text-muted-foreground">Success Rate</span><span className="font-semibold text-sky-500">{s?.success_rate ?? 0}%</span></div>
      </div>
    </CardContent></Card>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Block title="Today" s={data?.today} />
        <Block title="7 Days" s={data?.last_7_days} />
        <Block title="30 Days" s={data?.last_30_days} />
      </div>
      <Card className="border-border"><CardContent className="p-4">
        <div className="text-sm font-semibold mb-3">Authentication — 7 Hari Terakhir</div>
        <div style={{ width: '100%', height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data?.chart || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="success" name="Success" fill="#10b981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="failed" name="Failed" fill="#f43f5e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent></Card>
    </div>
  );
}

export default function RadiusServer() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get('/radius/status'); setStatus(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const fr = status?.freeradius || {};
  const dbo = status?.database || {};

  const tabs = [
    { key: 'dashboard', label: 'Dashboard', icon: Activity },
    { key: 'connection', label: 'Koneksi', icon: Plug },
    { key: 'users', label: 'RADIUS Users', icon: Users },
    { key: 'groups', label: 'Group / Role', icon: ShieldCheck },
    { key: 'nas', label: 'NAS / MikroTik', icon: RouterIcon },
    { key: 'nasgroups', label: 'NAS Groups', icon: Layers },
    { key: 'logs', label: 'Auth Log', icon: ScrollText },
    { key: 'sessions', label: 'Active Session', icon: Signal },
    { key: 'statistics', label: 'Statistics', icon: Activity },
    { key: 'audit', label: 'Audit Log', icon: KeyRound },
  ];

  return (
    <div className="space-y-4" data-testid="radius-server-page">
      <Breadcrumb items={[{ label: 'Master Settings' }, { label: 'Radius Server' }]} />
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-lg bg-sky-500/10 border border-sky-500/30"><Radio className="w-4 h-4 text-sky-500" /></span>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Radius Server</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">Kelola FreeRADIUS (SQL/MariaDB) — user, group, NAS, log & session. Password & shared secret tidak pernah ditampilkan.</p>
        </div>
        <div className="flex items-center gap-2">
          {dbo.online ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30"><Database className="w-3 h-3 mr-1" />DB Online</Badge> : <Badge className="bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-500/30"><Database className="w-3 h-3 mr-1" />DB Offline</Badge>}
          {fr.online ? <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-500/30"><Server className="w-3 h-3 mr-1" />FreeRADIUS Online</Badge> : <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-500/30"><Server className="w-3 h-3 mr-1" />FreeRADIUS {fr.state || 'Offline'}</Badge>}
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1">
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key} className="text-xs" data-testid={`radius-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 mr-1.5" />{t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <div className="mt-4">
          <TabsContent value="dashboard"><DashboardTab status={status} loading={loading} onRefresh={loadStatus} /></TabsContent>
          <TabsContent value="connection"><RadiusConnection onSaved={loadStatus} /></TabsContent>
          <TabsContent value="users"><RadiusUsers onChanged={loadStatus} /></TabsContent>
          <TabsContent value="groups"><RadiusGroups onChanged={loadStatus} /></TabsContent>
          <TabsContent value="nas"><RadiusNas onChanged={loadStatus} /></TabsContent>
          <TabsContent value="nasgroups"><RadiusNasGroups onChanged={loadStatus} /></TabsContent>
          <TabsContent value="logs"><AuthLogTab /></TabsContent>
          <TabsContent value="sessions"><SessionsTab /></TabsContent>
          <TabsContent value="statistics"><StatisticsTab /></TabsContent>
          <TabsContent value="audit"><AuditLogTab /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
